<template>
  <div class="contents">
    <div class="d-flex section-title-with-back">
      <img src="@/assets/images/Icon awesome-pen.png" alt width="22" height="22" />
      <p class="title-bk">生徒情報</p>
    </div>
    <div class="d-flex section-subtitle-with-back">
      <router-link :to="{ name: id ? 'student' : 'home' }">戻る</router-link>
    </div>
    <div class="container">
      <table class="table">
        <tbody>
          <tr>
            <td colspan="2">
              <button type="button" class="btn" @click="changePassword">
                パスワード変更
              </button>
              <button type="button" class="btn btn-edit" @click="editStudent" v-if="isEditStudent">
                編集
              </button>
              <button type="button" class="btn btn-edit" @click="editLearningHistory" v-if="isEditLearningHistory">
                学習履歴
              </button>
            </td>
          </tr>
          <tr>
            <th scope="row" width="30%">法人名（法人ID）</th>
            <td>{{ currentCorporationDisplay }}</td>
          </tr>
          <tr>
            <th scope="row">ブランド名（ブランドID）</th>
            <td>{{ currentBrandDisplay }}</td>
          </tr>
          <tr>
            <th scope="row">教場名（教場ID）</th>
            <td>{{ currentSchoolDisplay }}</td>
          </tr>
          <tr>
            <th scope="row">コース名（コースID）</th>
            <td>{{ currentCourseDisplay }}</td>
          </tr>
          <tr>
            <th scope="row">クラス名（クラスID）</th>
            <td>{{ currentTamaiClassDisplay }}</td>
          </tr>
          <tr>
            <th scope="row">生徒ID</th>
            <td>{{ currentStudent.uid }}</td>
          </tr>
          <tr>
            <th scope="row">生徒名</th>
            <td>{{ currentStudent.name }}</td>
          </tr>
          <tr>
            <th scope="row">生徒名（フリガナ）</th>
            <td>{{ currentStudent.nameKana }}</td>
          </tr>
          <tr>
            <th scope="row">生徒名（ローマ字）</th>
            <td>{{ currentStudent.nameRomaji }}</td>
          </tr>
          <tr>
            <th scope="row">生年月</th>
            <td>{{ currentStudent.yearOfBirth + '年' + currentStudent.monthOfBirth + '月' }}</td>
          </tr>
          <tr>
            <th scope="row">性別</th>
            <td>{{ currentStudentGender }}</td>
          </tr>
          <tr>
            <th scope="row">国籍</th>
            <td>{{ currentStudentCountryOfCitizenship }}</td>
          </tr>
          <tr>
            <th scope="row">居住国</th>
            <td>{{ currentStudentCountryOfResidence }}</td>
          </tr>
          <tr>
            <th scope="row">学年</th>
            <td>{{ currentStudentSchoolYear }}</td>
          </tr>
          <tr>
            <th scope="row">住所</th>
            <td>{{ currentStudent.address }}</td>
          </tr>
          <th scope="row">利用教材</th>
          <td>
            <div v-for="(material, index) in teachingMaterialList" :key="index">
              <span>{{ material }} <br /></span>
            </div>
          </td>
          <tr>
            <th scope="row">休会開始月</th>
            <td>{{ currentStudentDeactivateMonth }}</td>
          </tr>
          <tr>
            <th scope="row">教材変更 – 変更適用月</th>
            <td>{{ currentStudentNextTeachingMaterialMonth }}</td>
          </tr>
          <tr>
            <th scope="row">教材変更 – 変更後教材</th>
            <td>
              <div v-if="nextTeachingMaterialList.length > 0">
                <div v-for="(material, index) in nextTeachingMaterialList" :key="index">
                  <span>{{ material }} <br /></span>
                </div>
              </div>
              <div v-else>―</div>
            </td>
          </tr>
          <tr>
            <th scope="row">利用開始月</th>
            <td>{{ currentPreRegistrationDatetime }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import store from '@/store/index.js';
import {
  basicCorporationNameIdPresent,
  basicBrandNameIdPresent,
  basicSchoolNameIdPresent,
  basicCourseNameIdPresent,
  basicTamaiClassNameIdPresent,
  basicJapaneseYearMonth,
  basicNextTeachingMaterialMonth,
  basicTeachingMaterialShapeDisplay,
  basicTeachingMaterialAlgebraDisplay,
  basicTeachingMaterialJapaneseDisplay,
  basicTeachingMaterialKidsMathematicsDisplay,
  basicTeachingMaterialAdvancedMathematicsDisplay,
  basicSubFieldPresent,
} from '@/presenters/fields';

export default {
  props: {
    id: {
      type: [String, Number],
      required: false,
    },
  },
  beforeRouteEnter(routeTo, routeFrom, next) {
    if (routeTo.params.id) {
      store.dispatch('student/fetchStudent', routeTo.params.id).then(() => {
        next();
      });
    } else {
      next();
    }
  },
  mounted() {
    // 登録・編集の入力情報を初期化
    this.clearEditingStudent();
  },
  computed: {
    currentStudent() {
      return this.student;
    },
    currentCorporationDisplay() {
      return basicCorporationNameIdPresent(this.currentStudent.corporation);
    },
    currentBrandDisplay() {
      return basicBrandNameIdPresent(this.currentStudent.brand);
    },
    currentSchoolDisplay() {
      return basicSchoolNameIdPresent(this.currentStudent.school);
    },
    currentCourseDisplay() {
      return basicCourseNameIdPresent(this.currentStudent.course);
    },
    currentTamaiClassDisplay() {
      return basicTamaiClassNameIdPresent(this.currentStudent.tamaiClass);
    },
    currentStudentGender() {
      return basicSubFieldPresent(this.currentStudent, 'gender', 'name');
    },
    currentStudentCountryOfCitizenship() {
      return basicSubFieldPresent(this.currentStudent, 'citizenship', 'companyjp');
    },
    currentStudentCountryOfResidence() {
      return basicSubFieldPresent(this.currentStudent, 'residence', 'companyjp');
    },
    currentStudentSchoolYear() {
      return basicSubFieldPresent(this.currentStudent, 'schoolYear', 'name');
    },
    currentStudentShapeTeachingMaterial() {
      return basicTeachingMaterialShapeDisplay(this.currentStudent.currentLevelMaterials);
    },
    currentStudentAlgebraTeachingMaterial() {
      return basicTeachingMaterialAlgebraDisplay(this.currentStudent.currentLevelMaterials);
    },
    currentStudentJananeseTeachingMaterial() {
      return basicTeachingMaterialJapaneseDisplay(this.currentStudent.currentLevelMaterials);
    },
    currentStudentKidsMathematicsTeachingMaterial() {
      return basicTeachingMaterialKidsMathematicsDisplay(this.currentStudent.currentLevelMaterials);
    },
    currentStudentAdvancedMathematicsTeachingMaterial() {
      return basicTeachingMaterialAdvancedMathematicsDisplay(this.currentStudent.currentLevelMaterials);
    },
    currentStudentDeactivateMonth() {
      return basicJapaneseYearMonth(this.currentStudent.deactivatedDatetime);
    },
    currentStudentNextTeachingMaterialMonth() {
      return basicNextTeachingMaterialMonth(this.currentStudent.nextFiguredTeachingMaterials);
    },
    currentStudentNextShapeTeachingMaterial() {
      return basicTeachingMaterialShapeDisplay(this.currentStudent.nextLevelMaterials);
    },
    currentStudentNextAlgebraTeachingMaterial() {
      return basicTeachingMaterialAlgebraDisplay(this.currentStudent.nextLevelMaterials);
    },
    currentStudentNextJapaneseTeachingMaterial() {
      return basicTeachingMaterialJapaneseDisplay(this.currentStudent.nextLevelMaterials);
    },
    currentStudentNextKidsMathematicsTeachingMaterial() {
      return basicTeachingMaterialKidsMathematicsDisplay(this.currentStudent.nextLevelMaterials);
    },
    currentStudentNextAdvancedMathematicsTeachingMaterial() {
      return basicTeachingMaterialAdvancedMathematicsDisplay(this.currentStudent.nextLevelMaterials);
    },
    currentPreRegistrationDatetime() {
      if (this.currentStudent.preRegistrationMonth) {
        return this.currentStudent.preRegistrationMonth.text;
      }
      return basicJapaneseYearMonth(this.currentStudent.preRegistrationDatetime);
    },
    isEditStudent() {
      return this.allowEditingStudent && !this.hasTeacherPrivilege;
    },
    isEditLearningHistory() {
      return this.allowEditingStudent && this.hasTeacherPrivilege;
    },
    teachingMaterialList() {
      let materialList = [
        this.currentStudentKidsMathematicsTeachingMaterial,
        this.currentStudentJananeseTeachingMaterial,
        this.currentStudentShapeTeachingMaterial,
        this.currentStudentAlgebraTeachingMaterial,
        this.currentStudentAdvancedMathematicsTeachingMaterial,
      ];
      materialList = materialList.filter((material) => {
        return material != '―';
      });
      return materialList;
    },
    nextTeachingMaterialList() {
      let materialList = [
        this.currentStudentNextKidsMathematicsTeachingMaterial,
        this.currentStudentNextJapaneseTeachingMaterial,
        this.currentStudentNextShapeTeachingMaterial,
        this.currentStudentNextAlgebraTeachingMaterial,
        this.currentStudentNextAdvancedMathematicsTeachingMaterial,
      ];
      materialList = materialList.filter((material) => {
        return material != '―';
      });
      return materialList;
    },
    ...mapState({
      student: (state) => state.student.student,
    }),
    ...mapGetters('student', ['getStudentById']),
    ...mapGetters('role', ['allowEditingStudent', 'hasAdminPrivilege', 'hasTeacherPrivilege']),
  },
  methods: {
    editStudent() {
      if (this.id) {
        this.$logger.debug('Edit Student ID ' + this.id);
        this.$router.push({ name: 'student-id-edit', params: { id: this.id } });
      }
    },
    editLearningHistory() {
      if (this.id) {
        this.$logger.debug('Edit Learning History Student ID ' + this.id);
        this.$router.push({ name: 'student-id-history', params: { id: this.id } });
      }
    },
    changePassword() {
      if (this.id) {
        this.$logger.debug('Change Password ID ' + this.id);
        this.$router.push({ name: 'student-id-change-password', params: { id: this.id } });
      } else {
        this.$logger.debug('Change Password');
        this.$router.push({ name: 'student-change-password' });
      }
    },
    ...mapActions('student', ['clearEditingStudent']),
  },
};
</script>

<style scoped>
.btn {
  margin-left: 0px;
}
.btn-edit {
  margin-left: 20px;
}
</style>
